import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteRole, fetchRoleList } from '#/api/system/role';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import { buildRoleColumns } from '#/modules/infra/settings/access/data/roleColumns';
import useAccessList, { ACCESS_LIST_PAGE_SIZE } from '#/modules/infra/settings/access/hooks/useAccessList';
import { toast } from '#/utils/toast';

const ROLE_LIST_QUERY_KEY = ['system', 'role', 'list'];
const ROLE_DEFAULT_SORT = [{ id: 'code', desc: false }];

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

const RoleListPage = () => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canCreate = useCan(PERM.SYS_ROLE_CREATE);
  const canUpdate = useCan(PERM.SYS_ROLE_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useAccessList({
    queryKeyBase: ROLE_LIST_QUERY_KEY,
    fetchList: fetchRoleList,
    defaultSort: ROLE_DEFAULT_SORT,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteRole(id),
    onSuccess: (result) => {
      toast.success(result?.message || '已刪除角色');
      queryClient.invalidateQueries({ queryKey: ['system', 'role'] });
    },
    onError: (deleteError) => {
      toast.error(getErrorMessage(deleteError, '刪除失敗'));
    },
  });

  const handleDelete = useCallback(async (row) => {
    if (row.code === 'admin') {
      toast.error('admin 角色不可刪除');
      return;
    }
    const ok = await confirm({
      title: '刪除角色',
      message: `確定刪除「${row.name || row.code}」？已指派使用者時後端會拒絕。`,
      confirmText: '刪除',
      destructive: true,
      cancelText: '取消',
    });
    if (!ok) {
      return;
    }
    deleteMutation.mutate(row.id);
  }, [confirm, deleteMutation]);

  const columns = useMemo(
    () => buildRoleColumns({
      canUpdate,
      isDeleting: deleteMutation.isPending,
      onDelete: handleDelete,
    }),
    [canUpdate, deleteMutation.isPending, handleDelete],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({ id: item.id, desc: Boolean(item.desc) })));
  }, [setSort]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.systemRoleCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      {ConfirmDialogHost}
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">職務／角色</span>
        </h1>
        <div className="table-panel min-h-0 flex-1">
          <ListTablePanel
            tableKey="sys.role.list"
            columns={columns}
            rows={rows}
            pagination={pagination}
            initialPageSize={ACCESS_LIST_PAGE_SIZE}
            isLoading={isLoading}
            isError={isError}
            errorMessage={isError ? getErrorMessage(error, '載入角色列表失敗') : undefined}
            onRetry={isError ? () => { refetch(); } : undefined}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSearch={setKeyword}
            onReset={resetListQuery}
            initialKeyword={listQuery.keyword}
            searchPlaceholder="搜尋代碼或名稱"
            headerAction={headerAction}
            listQuery={listQuery}
            onSortRemove={removeSortAt}
            onSortingChange={handleSortingChange}
            tableClassName="table_compact"
            textSize="sm"
          />
        </div>
      </div>
    </PageShell>
  );
};

export default RoleListPage;
